import { useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { notifyApiUser, notifyApiUserAction } from '../../common/apiNotification';
import { MY_GAMES_PATH } from '../shared/gamesBrowseUrl';
import { notifyImportComplete } from './importComplete';
import {
  buildImportCompleteMessage,
  importFailureMessage,
  importJobMatchesPending,
  importPollDelayMs,
  IMPORT_POLL_MAX_MS,
  IMPORT_POLL_TIMEOUT_MESSAGE,
  isActiveImportJob,
} from './importPollHelpers';
import { fetchImportStatus, topUpMyGameAnalysisBuffer } from './myGamesApi';
import {
  addPendingImport,
  clearPendingImport,
  PENDING_IMPORT_ADDED_EVENT,
  readPendingImport,
} from './pendingImportTracker';

export function usePendingImportPoll(): void {
  const { isLoggedIn } = useAuth();

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let pollTimeoutId: number | undefined;
    let cancelled = false;

    const clearPollTimeout = () => {
      if (pollTimeoutId != null) {
        window.clearTimeout(pollTimeoutId);
        pollTimeoutId = undefined;
      }
    };

    const schedulePoll = () => {
      clearPollTimeout();
      if (cancelled) {
        return;
      }

      const pending = readPendingImport();
      if (!pending) {
        return;
      }

      pollTimeoutId = window.setTimeout(() => {
        void poll().finally(() => {
          if (!cancelled && readPendingImport()) {
            schedulePoll();
          }
        });
      }, importPollDelayMs(pending.startedAt));
    };

    const seedPendingImportIfNeeded = async () => {
      if (readPendingImport()) {
        return;
      }

      try {
        const status = await fetchImportStatus();
        const importJob = status.importJob;
        if (
          importJob?.status !== 'pending' ||
          !importJob.platform ||
          !importJob.username ||
          !isActiveImportJob(importJob.updatedAt)
        ) {
          return;
        }

        const startedAtMs = new Date(importJob.updatedAt!).getTime();
        addPendingImport({
          platform: importJob.platform,
          username: importJob.username,
          priorCount: status.importedGameCount,
          startedAt: startedAtMs,
        });
      } catch {
        /* ignore */
      }
    };

    const handleImportComplete = async (
      status: Awaited<ReturnType<typeof fetchImportStatus>>,
      pending: NonNullable<ReturnType<typeof readPendingImport>>,
    ) => {
      clearPendingImport();
      clearPollTimeout();
      void topUpMyGameAnalysisBuffer().catch(() => undefined);
      notifyImportComplete({ importedGameCount: status.importedGameCount });
      notifyApiUserAction({
        message: buildImportCompleteMessage(status, pending),
        actionLabel: 'View games',
        actionTo: MY_GAMES_PATH,
        variant: 'gold',
      });
    };

    const poll = async () => {
      const pending = readPendingImport();
      if (!pending) {
        clearPollTimeout();
        return;
      }

      if (Date.now() - pending.startedAt >= IMPORT_POLL_MAX_MS) {
        clearPendingImport();
        clearPollTimeout();
        notifyApiUser(IMPORT_POLL_TIMEOUT_MESSAGE);
        return;
      }

      try {
        const status = await fetchImportStatus();
        const importJob = status.importJob;

        if (
          importJob?.status === 'failed' &&
          importJobMatchesPending(importJob, pending)
        ) {
          clearPendingImport();
          clearPollTimeout();
          notifyApiUser(
            importFailureMessage(
              importJob,
              pending.platform,
              pending.username,
            ),
          );
          return;
        }

        if (
          importJob?.status === 'complete' &&
          importJobMatchesPending(importJob, pending)
        ) {
          await handleImportComplete(status, pending);
          return;
        }

        if (status.importedGameCount > pending.priorCount) {
          await handleImportComplete(status, pending);
        }
      } catch {
        /* keep polling until timeout */
      }
    };

    // Wake the poll loop when an import is queued after this effect ran
    // (e.g. from the import dialog), instead of waiting for a page refresh.
    const handlePendingImportAdded = () => {
      if (!cancelled) {
        schedulePoll();
      }
    };
    window.addEventListener(
      PENDING_IMPORT_ADDED_EVENT,
      handlePendingImportAdded,
    );

    void seedPendingImportIfNeeded().then(() => {
      if (cancelled) {
        return;
      }
      void poll().finally(() => {
        if (!cancelled && readPendingImport()) {
          schedulePoll();
        }
      });
    });

    return () => {
      cancelled = true;
      clearPollTimeout();
      window.removeEventListener(
        PENDING_IMPORT_ADDED_EVENT,
        handlePendingImportAdded,
      );
    };
  }, [isLoggedIn]);
}
